import { DiscountType } from '@repo/common';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Separator,
    cn,
} from '@repo/shadcn';

import { PaymentStatusBadge } from '@/components/ui/badge';
import { calculateTotalDiscountValue } from '@/utils/calculate-total-discount.util';
import { formatCurrencyInBDT } from '@/utils/currency-formater';

import { useOrderDetailsContext } from './context';

export const InvoiceDetails = () => {
    const { data } = useOrderDetailsContext();

    const renderItems = () => {
        return data?.data?.items
            .map((item) => item)
            .sort((a, b) => {
                if (
                    a.product_name?.toLowerCase() <
                    b.product_name?.toLowerCase()
                )
                    return -1;
                if (
                    a.product_name?.toLowerCase() >
                    b.product_name?.toLowerCase()
                )
                    return 1;
                return 0;
            })
            .map((item) => {
                const itemSubTotal = item.price * item.quantity;
                const itemTotal =
                    itemSubTotal -
                    (item.discount_type === DiscountType.Fixed
                        ? item.discount_value || 0
                        : ((item?.discount_value ?? 0) / 100) * itemSubTotal);

                return (
                    <div
                        key={item.id}
                        className="grid grid-cols-8 gap-4 text-sm"
                    >
                        <div className="col-span-3 font-medium">
                            {item.product_name}
                        </div>
                        <div className="col-span-1 text-right">
                            {formatCurrencyInBDT(item.price)}
                        </div>
                        <div className="col-span-1 text-right">
                            {item.quantity}
                        </div>
                        <div className="col-span-1 text-right">
                            {item.discount_type === DiscountType.Fixed ? (
                                formatCurrencyInBDT(item.discount_value || 0)
                            ) : (
                                <span>{item.discount_value}%</span>
                            )}
                        </div>
                        <div className="col-span-1 text-right font-medium">
                            {formatCurrencyInBDT(itemSubTotal)}
                        </div>

                        <div className="col-span-1 text-right font-medium">
                            {formatCurrencyInBDT(itemTotal)}
                        </div>
                    </div>
                );
            });
    };

    const subtotal =
        data?.data?.items?.reduce((acc, item) => {
            return acc + item.price * item.quantity;
        }, 0) || 0.0;
    // const discount =
    //     data?.data?.discount_type === DiscountType.Fixed
    //         ? data?.data?.discount_value || 0.0
    //         : `${data?.data?.discount_value || 0.0}%`;
    const total = data?.data?.payment?.total_price || 0.0;
    const totalPaid = data?.data?.payment?.total_paid || 0.0;
    const totalDue = total - totalPaid;
    const orderStatus = data?.data?.status || '---';

    const totalDiscountValue = calculateTotalDiscountValue(
        (data?.data as any) ?? {},
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg">Items</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Items Table */}
                <div className="space-y-4">
                    <div className="grid grid-cols-8 gap-4 text-sm font-semibold text-muted-foreground">
                        <div className="col-span-3">Name</div>
                        <div className="col-span-1 text-right">Price</div>
                        <div className="col-span-1 text-right">Quantity</div>
                        <div className="col-span-1 text-right">Discount</div>
                        <div className="col-span-1 text-right">Sub Total</div>
                        <div className="col-span-1 text-right">Total</div>
                    </div>
                    <Separator />
                    {renderItems()}
                </div>

                <Separator />

                {/* Summary Section */}
                <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">
                            Sub Total:
                        </span>
                        <span className="font-semibold">
                            {/*{subtotal.toFixed(2)}*/}
                            {formatCurrencyInBDT(subtotal)}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Discount:</span>

                        <span className="font-semibold">
                            {formatCurrencyInBDT(totalDiscountValue)}
                        </span>
                    </div>

                    <div className="flex justify-between border-t pt-3">
                        <span className="font-semibold">Total:</span>
                        <span className="text-lg font-bold">
                            {formatCurrencyInBDT(total)}
                        </span>
                    </div>
                </div>

                <Separator />

                {/* Payment Details */}
                <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">
                            Total Paid:
                        </span>
                        <span className="font-medium">
                            {/*{totalPaid.toFixed(2)}*/}
                            {formatCurrencyInBDT(totalPaid)}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">
                            Total Due:
                        </span>
                        <span
                            className={cn('font-medium', {
                                'text-red-600': totalDue > 0,
                                '': totalDue <= 0,
                            })}
                        >
                            {formatCurrencyInBDT(totalDue)}
                        </span>
                    </div>
                    <div className="flex justify-between border-t pt-3">
                        <span className="font-semibold">Payment Status:</span>
                        <PaymentStatusBadge status={orderStatus} />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

InvoiceDetails.displayName = 'InvoiceDetails';
